import React, { useState, useEffect } from 'react';
import './ApprovedAccounts.css';

const RejectedMasterAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [accountsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch rejected master accounts from API
  const fetchRejectedAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('https://server.tradefxonline.com/admin_master_rejected.php', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setAccounts(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to fetch rejected master accounts');
      }
    } catch (err) {
      console.error('Error fetching rejected master accounts:', err);
      setError(err.message || 'Failed to load rejected master accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRejectedAccounts();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchRejectedAccounts();
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    (account.strategyname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (account.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (account.trade_id || '').toString().includes(searchTerm) ||
    (account.account_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastAccount = currentPage * accountsPerPage;
  const indexOfFirstAccount = indexOfLastAccount - accountsPerPage;
  const currentAccounts = filteredAccounts.slice(indexOfFirstAccount, indexOfLastAccount);
  const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage);

  return (
    <div className="approved-accounts-container">
      <div className="page-header">
        <h1 className="page-title">Rejected Master Accounts</h1>
        <button 
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="refresh-btn"
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={handleRefresh} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by strategy name, email, trade ID, or account name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          disabled={loading}
        />
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading rejected master accounts...</p>
        </div>
      ) : (
        <div className="accounts-table-container">
          <table className="accounts-table">
            <thead>
              <tr>
                <th>Strategy Image</th>
                <th>Strategy Name</th>
                <th>Account Name</th>
                <th>Email</th>
                <th>Trade ID</th>
                <th>Server</th>
                <th>Platform</th>
                <th>Commission</th>
                <th>Min Deposit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {currentAccounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    <div className="strategy-image-container">
                      <img
                        src={account.strategyimage ? `https://server.tradefxonline.com/${account.strategyimage}` : '/image.png'}
                        alt={account.strategyname}
                        className="strategy-image"
                        onError={(e) => {
                          e.target.src = '/image.png';
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="strategy-name">
                      <strong>{account.strategyname || 'N/A'}</strong>
                      <div className="strategy-about">
                        {account.strategyabout || 'No description'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="account-type">
                      {account.account_name || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="email-cell">
                      {account.email || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="trade-id">
                      {account.trade_id || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="server-info">
                      {account.server || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="platform-info">
                      {account.tradePlatform || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="commission">
                      {account.commission || '0'}%
                    </div>
                  </td>
                  <td>
                    <div className="commission">
                      ${account.mindeposit || '0'}
                    </div>
                  </td>
                  <td>
                    <span className="status-badge rejected">
                      Rejected
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredAccounts.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstAccount + 1} to {Math.min(indexOfLastAccount, filteredAccounts.length)} of {filteredAccounts.length} accounts
          </div>
          <div className="pagination-controls">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <button
                  key={number}
                  onClick={() => setCurrentPage(number)}
                  className={`page-number ${currentPage === number ? 'active' : ''}`}
                >
                  {number}
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {!loading && filteredAccounts.length === 0 && !error && (
        <div className="no-data-message">
          <p>No rejected master accounts found.</p>
        </div>
      )}
    </div>
  );
};

export default RejectedMasterAccounts;
